import { Alert as MuiAlert, Box, Divider, Stack, Typography, useTheme } from '@mui/material'

import { Button, Modal } from '@repo/ui'
import { ArrowUpRightIcon, XIcon } from 'lucide-react'

import { StatusChip } from 'components/display'
import { MARKET_STAT_STATUSES } from 'constants/statuses'
import { date } from 'utils'
import type { IAskClaim } from 'services/askClaimsApi'

/**
 * The full-context view behind a click on the Stats-tab table's quote cell (VRG-445
 * review-table rebuild). Renders exactly what the old card design showed — the
 * verbatim quote, its surrounding paragraph, and BOTH provenance hops side by side —
 * because that content is what made the table acceptable in the first place: the
 * table's own quote column is 2-line-clamped, so anything past that needs a place to
 * go, and the drawer is it.
 *
 * ## Why a drawer, not an inline expand
 *
 * A MODAL, not a drawer, which reverses an earlier call in this file.
 *
 * The drawer's argument was that reviewing is processing a QUEUE and the queue should
 * stay visible beside it. True, and not enough: a drawer is this app's FILTER surface,
 * so using it for a decision made a judgement read as configuration, and at 500px the
 * two provenance hops had nowhere to sit. A modal is what the product already uses when
 * it wants a decision. Previous/Next carry over unchanged, so the one thing the drawer
 * genuinely did better is kept.
 *
 * Was: matched `CertificatePreviewDrawer` (§28/§47 — same house pattern, same 500px
 * width, same inset/rounded chrome) rather than a new pattern. It is a VIEWING
 * surface only — Approve/Reject stay on the row's kebab and the bulk bar, not
 * duplicated here, so there is exactly one place each action lives.
 *
 * ## Why the row itself doesn't carry `onRowClick`
 *
 * The shared `Table` has no `onRowClick` pass-through (it only exposes per-cell
 * `renderCell`), so this opens from the quote cell's own click handler — the same
 * convention the Content tab already uses for its Title cell.
 */

/** One labelled provenance hop — ported from the old card verbatim (Hop). */
const Hop = ({
  label,
  name,
  url,
  emphasis,
}: {
  label: string
  name: string | null
  url: string | null
  emphasis?: boolean
}) => (
  <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
    <Typography variant="body3" color="text.secondary">
      {label}
    </Typography>
    {name ? (
      url ? (
        <Stack
          component="a"
          href={url}
          target="_blank"
          rel="noreferrer"
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ color: 'inherit', textDecoration: 'none', minWidth: 0 }}
        >
          <Typography variant="body3" sx={{ fontWeight: emphasis ? 600 : 400 }}>
            {name}
          </Typography>
          <ArrowUpRightIcon strokeWidth={1} size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
        </Stack>
      ) : (
        <Typography variant="body3" sx={{ fontWeight: emphasis ? 600 : 400 }}>
          {name}
        </Typography>
      )
    ) : (
      // "Not stated" is rendered rather than the row being hidden — a missing
      // original source is the single most important thing a reviewer can know
      // about a proposal (§67-shaped reasoning: hiding the row would make the
      // riskiest case the quietest one).
      <Typography variant="body3" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        Not stated
      </Typography>
    )}
  </Stack>
)

interface MarketClaimDetailModalProps {
  open: boolean
  onClose: () => void
  stat: IAskClaim | null
  /** Approve this one. Absent for a stat that is not pending — see the footer. */
  onApprove?: () => void
  /** Open the reject-reason modal for this one. */
  onReject?: () => void
  /** Move to the neighbouring row WITHOUT closing — see the footer's note. */
  onPrevious?: () => void
  onNext?: () => void
  isActing?: boolean
}

export const MarketClaimDetailModal = ({
  open,
  onClose,
  stat,
  onApprove,
  onReject,
  onPrevious,
  onNext,
  isActing = false,
}: MarketClaimDetailModalProps) => {
  const theme = useTheme()

  const status = stat?.approvedAt ? 'approved' : stat?.rejectedAt ? 'rejected' : 'pending'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Claim"
      sx={{ maxWidth: 720 }}
      actions={
        stat ? (
          <>
            {/* Prev/Next keep the queue walkable — the one thing the drawer did better
                than a modal, kept rather than lost in the conversion. */}
            <Button variant="secondary" onClick={onPrevious} disabled={!onPrevious || isActing}>
              Previous
            </Button>
            <Button variant="secondary" onClick={onNext} disabled={!onNext || isActing}>
              Next
            </Button>
            <Box sx={{ flex: 1 }} />
            {status === 'pending' && (
              <>
                <Button
                  variant="secondary"
                  destructive="hover"
                  onClick={onReject}
                  disabled={!onReject || isActing}
                >
                  Reject
                </Button>
                <Button
                  variant="primary"
                  onClick={onApprove}
                  disabled={!onApprove}
                  loading={isActing}
                >
                  Approve
                </Button>
              </>
            )}
          </>
        ) : null
      }
    >
      {stat && (
        <Box sx={{ mb: 4 }}>
          <StatusChip statuses={MARKET_STAT_STATUSES} status={status} size="small" />
        </Box>
      )}

      <Box>
        {stat && (
          <Stack spacing={4}>
            <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
              “{stat.verbatimQuote}”
            </Typography>

            {stat.contextSnippet && (
              <Typography
                variant="body3"
                color="text.secondary"
                sx={{ lineHeight: 1.6, fontStyle: 'italic' }}
              >
                {stat.contextSnippet}
              </Typography>
            )}

            {/*
              THE CAVEATS SIT ABOVE THE APPROVE BUTTON, not below the fold.

              This is the whole point of storing them. A reviewer decides here, and a
              caveat they have to scroll for is a caveat that does not affect the
              decision. Each block below changes whether the figure ANSWERS the question
              being asked of it, which is a different thing from whether the quote is
              accurate — the substring check already settled that.

              Warning severity, not info, for the same reason working-conventions §43 gives
              for consequence copy: what accrues while a figure is wrong is not recoverable
              by fixing it later. A number cited into a customer answer has already been read.
            */}
            {stat.isDerived && (
              <MuiAlert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body3" sx={{ fontWeight: 600, display: 'block' }}>
                  This is our arithmetic, not the source's published figure.
                </Typography>
                {stat.derivation && (
                  <Typography variant="body3" sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}>
                    {stat.derivation}
                  </Typography>
                )}
              </MuiAlert>
            )}

            {stat.evidenceTier === 'calibration' && (
              <MuiAlert severity="warning">
                <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
                  Calibration only. This source sits below the evidence bar and must not be
                  cited as a peer of the others.
                </Typography>
              </MuiAlert>
            )}

            {stat.scopeCaveat && (
              <MuiAlert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body3" sx={{ fontWeight: 600, display: 'block' }}>
                  Scope
                </Typography>
                <Typography variant="body3" sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}>
                  {stat.scopeCaveat}
                </Typography>
              </MuiAlert>
            )}

            {stat.verificationNote && (
              <Box>
                <Typography variant="body3" color="text.secondary" sx={{ display: 'block' }}>
                  How this was verified
                </Typography>
                <Typography variant="body3" sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}>
                  {stat.verificationNote}
                </Typography>
              </Box>
            )}

            {/*
              Deliberately NOT styled as an endorsement. Site usage is a fact about what we
              publish, not evidence the figure is right — and the audit that produced these
              rows found site usage correlates with NEEDING review, not with having had it.
              So it reads as a note, and the approve button beside it is unaffected.
            */}
            {stat.siteUsage && stat.siteUsage.length > 0 && (
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Cited on the marketing site: {stat.siteUsage.join(', ')}. That does not mean it
                has been reviewed.
              </Typography>
            )}

            <Divider />

            {/* BOTH hops, side by side — the whole anti-laundering design is visible
                here or it is nowhere: "as reported by" is who we read, "original
                source" is who measured it. */}
            <Stack direction="row" spacing={5}>
              <Hop label="As reported by" name={stat.asReportedBy} url={stat.articleUrl} />
              <Hop
                label="Original source"
                name={stat.originalSource}
                url={stat.originalSourceUrl}
                emphasis
              />
            </Stack>

            {(stat.value !== null ||
              stat.category ||
              stat.region ||
              stat.period ||
              stat.sampleSize !== null) && (
              <Stack direction="row" spacing={5} flexWrap="wrap">
                {stat.value !== null && (
                  <Typography variant="body3" color="text.secondary">
                    Value: {stat.value}
                    {stat.unit ? ` ${stat.unit}` : ''}
                  </Typography>
                )}
                {stat.category && (
                  <Typography variant="body3" color="text.secondary">
                    Category: {stat.category}
                  </Typography>
                )}
                {stat.region && (
                  <Typography variant="body3" color="text.secondary">
                    Region: {stat.region}
                  </Typography>
                )}
                {/* Shown only when known. An ABSENT n is not rendered as "n: —" because
                    that reads as "not applicable"; where it could not be confirmed, the
                    verification note above says so in words. */}
                {stat.sampleSize !== null && (
                  <Typography variant="body3" color="text.secondary">
                    Sample: n={stat.sampleSize}
                  </Typography>
                )}
                {stat.period && (
                  <Typography variant="body3" color="text.secondary">
                    Period: {stat.period}
                  </Typography>
                )}
              </Stack>
            )}

            {stat.publishedAt && (
              <Typography variant="body3" color="text.secondary">
                Article published {date(stat.publishedAt)}
              </Typography>
            )}

            {stat.rejectedAt && stat.rejectionReason && (
              <>
                <Divider />
                <Stack spacing={1}>
                  <Typography variant="body3" color="text.secondary">
                    Rejection reason
                  </Typography>
                  <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
                    {stat.rejectionReason}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Box>

    </Modal>
  )
}
